import { useState } from 'react';
import DataGrid from 'react-data-grid';
import 'react-data-grid/lib/styles.css';

import { CASLAction, useAbility } from '@app/utils/authUtils';
import { ContextMenuProps, rowKeyGetter } from '@app/utils/epgUtils';
import SaveIcon from '@mui/icons-material/Save';
import { Button } from '@mui/material';

import { ListHeader } from '@app/components/ListHeader';
import { HeaderActionTypes, ListHeaderActions } from '@app/components/ListHeaderActions';

import { ContextMenu } from './ContextMenu';
import { DialogCopySchedule } from './DialogCopySchedule';
import './styles.css';

export const EPGDataGrid = ({
  rows,
  fetchEpgByDate,
  onAddNew,
  onSave,
  onCellKeyDown,
  deleteRow,
  handleCopySchedule,
  columns,
  setRows,
}: any) => {
  const [contextMenuProps, setContextMenuProps] = useState<ContextMenuProps | null>(null);
  const [isCopyScheduleDialogOpen, setIsCopyScheduleDialogOpen] = useState<boolean>(false);
  const isContextMenuOpen = contextMenuProps !== null;

  const ability = useAbility();

  const handleClose = () => {
    setContextMenuProps(null);
  };

  const handleChangeContextMenuProps = (contextMenuProps: ContextMenuProps | null) => {
    setContextMenuProps(contextMenuProps);
  };

  const onOpenCopySchedule = () => {
    setIsCopyScheduleDialogOpen(true);
  };

  const handleDeleteRow = () => {
    deleteRow(contextMenuProps);

    handleClose();
  };

  const handleCloseCopySchedule = () => {
    setIsCopyScheduleDialogOpen(false);
  };

  const headerActions = [];

  if (ability.can(CASLAction.WRITE, 'epgs')) {
    headerActions.push(
      { id: 1, type: HeaderActionTypes.ADD_NEW, handler: onAddNew, label: 'ADD SEGMENT' },
      { id: 2, type: HeaderActionTypes.COPY, handler: onOpenCopySchedule, disabled: !!rows.length }
    );
  }

  return (
    <div className="generic-list_container">
      <ListHeader hideSearch title="EPG" handleChangeFilters={fetchEpgByDate} showEpgSearch />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {ability.can(CASLAction.WRITE, 'epgs') && (
          <Button
            data-cy="save-epg"
            variant="outlined"
            color="primary"
            sx={{ marginTop: '20px', marginLeft: '20px' }}
            startIcon={<SaveIcon />}
            onClick={onSave}>
            SAVE
          </Button>
        )}

        <ListHeaderActions headerActions={headerActions} />
      </div>
      {ability.can(CASLAction.DELETE, 'epgs') && (
        <div className="footer-grid">
          <span>* To delete a segment, select the row, then right-click and select 'Delete Row'</span>
        </div>
      )}
      <div className="main-grid">
        <DataGrid
          className="fill-grid rdg-light"
          rowKeyGetter={rowKeyGetter}
          columns={columns}
          defaultColumnOptions={{
            sortable: false,
            resizable: true,
          }}
          onCellContextMenu={({ row }, event) => {
            event.preventGridDefault();
            // Do not show the default context menu
            event.preventDefault();
            /**
              If the context menu ever has any options other than Delete Row,
              then the ability-handling here will have to be revisited.
             */
            if (ability.can(CASLAction.DELETE, 'epgs')) {
              setContextMenuProps({
                rowId: row.id,
                rowIdx: rows.indexOf(row),
                top: event.clientY,
                left: event.clientX,
              });
            }
          }}
          onCellKeyDown={onCellKeyDown}
          rows={rows}
          onRowsChange={setRows}
        />
      </div>
      {isCopyScheduleDialogOpen && (
        <DialogCopySchedule
          open={isCopyScheduleDialogOpen}
          handleClose={handleCloseCopySchedule}
          handleSubmit={handleCopySchedule}
        />
      )}
      {isContextMenuOpen && (
        <ContextMenu
          contextMenuProps={contextMenuProps}
          open={isContextMenuOpen}
          handleChangeContextMenuProps={handleChangeContextMenuProps}
          deleteRow={handleDeleteRow}
        />
      )}
    </div>
  );
};
